import { DprStatusTrackerAccumulation, PreviousDpr } from '@dpr-details/models';
import { StatusTrackerWithData } from '@shared/models/project-setup';
import {
  CarryOverDefaultValue,
  getStatusTrackerColumnCarryOver,
  isStatusTrackerColumnCarryOverEnabled,
  normalizeCarryOverValue,
} from './accumulation.utils';

/**
 * Resolves the value of every accumulated column, per status tracker and per row.
 * Status Tracker rows are fixed, so a row is matched against the previous DPR by
 * id rather than through an anchor column.
 *
 * A Formula source is read like any other: the previous DPR it is handed is
 * rebuilt from the DPR's previous values, which hold the formula's worked-out
 * value.
 */
export function computeStatusTrackerAccumulation(
  statusTrackers: StatusTrackerWithData[],
  previousDpr: PreviousDpr | null
): Map<number, DprStatusTrackerAccumulation> {
  const accumulations = new Map<number, DprStatusTrackerAccumulation>();

  statusTrackers?.forEach((statusTracker) => {
    const carryOverColumns = statusTracker.columns?.filter((column) =>
      isStatusTrackerColumnCarryOverEnabled(statusTracker, column)
    );

    if (!carryOverColumns?.length) {
      return;
    }

    const previousValues = previousDpr?.dprStatusTracker
      ? mapPreviousValues(statusTracker, previousDpr)
      : null;
    const valuesByColumnId = new Map<number, Map<number, string | null>>();

    carryOverColumns.forEach((column) => {
      const sourceColumnId = +getStatusTrackerColumnCarryOver(
        statusTracker,
        column
      ).carryOverItemId;
      const rowValues = new Map<number, string | null>();

      statusTracker.rows?.forEach((row) =>
        rowValues.set(
          +row.id,
          resolveValue(+column.id, sourceColumnId, +row.id, previousValues)
        )
      );

      valuesByColumnId.set(+column.id, rowValues);
    });

    accumulations.set(+statusTracker.id, {
      statusTrackerId: +statusTracker.id,
      valuesByColumnId,
    });
  });

  return accumulations;
}

function mapPreviousValues(
  statusTracker: StatusTrackerWithData,
  previousDpr: PreviousDpr
): Map<string, string | null> {
  const previousValues = new Map<string, string | null>();

  previousDpr?.dprStatusTracker?.dprStatusTrackerDetails
    ?.filter((detail) => detail.statusTrackerId === +statusTracker.id)
    ?.forEach((detail) =>
      previousValues.set(`${detail.columnId}_${detail.rowId}`, detail.value)
    );

  return previousValues;
}

function resolveValue(
  columnId: number,
  sourceColumnId: number,
  rowId: number,
  previousValues: Map<string, string | null> | null
): string | null {
  // A column carrying over from itself has nothing to read: the cell is read-only,
  // so its own history never holds anything but this same fallback. A first DPR has
  // nothing to read either, and a Status Tracker carry-over has no initial value.
  if (sourceColumnId === columnId || !previousValues) {
    return CarryOverDefaultValue;
  }

  // Status Tracker stores no cell for a blank value, so a missing one is blank.
  return normalizeCarryOverValue(
    previousValues.get(`${sourceColumnId}_${rowId}`)
  );
}
